/**
 * CloseChecklist — Close plan action items with one-click copy.
 *
 * Features:
 * - Add/edit/delete checklist items
 * - Toggle completion status
 * - Priority badges (low/medium/high/critical)
 * - MEDDICC dimension tags
 * - One-click copy of progress for Salesforce
 */

import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckSquare, Square, Plus, Trash2, Calendar, User,
  Loader2, ChevronDown, ChevronRight
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import CopyButton from './CopyButton';
import './CloseChecklist.css';

const PRIORITY_COLORS = {
  low: 'var(--color-text-muted)',
  medium: 'var(--color-primary)',
  high: 'var(--color-warning)',
  critical: 'var(--color-error)',
};

const CATEGORIES = [
  { value: 'meddicc', label: 'MEDDICC' },
  { value: 'technical', label: 'Technical' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'legal', label: 'Legal' },
  { value: 'internal', label: 'Internal' },
];

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function CloseChecklist({ opportunityId, checklist = [], progress, onUpdate }) {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', priority: 'medium', category: '', due_date: '' });
  const [expandedCategories, setExpandedCategories] = useState({});

  const addMutation = useMutation({
    mutationFn: (item) => apiClient.post(`/api/opportunities/${opportunityId}/checklist`, item),
    onSuccess: () => {
      setIsAdding(false);
      setNewItem({ title: '', priority: 'medium', category: '', due_date: '' });
      queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
      onUpdate?.();
    },
  });

  // Optimistic (UI-responsiveness review 2026-08-08, StepChecklist pattern):
  // the checkbox and the N/M header move on click; onMutate snapshots the
  // opportunity cache, patches item + progress counters, and onError rolls
  // back. The invalidate runs on settle as the truth-up.
  const updateMutation = useMutation({
    mutationFn: ({ itemId, updates }) => apiClient.put(`/api/opportunities/${opportunityId}/checklist/${itemId}`, updates),
    onMutate: async ({ itemId, updates }) => {
      await queryClient.cancelQueries({ queryKey: ['opportunity', opportunityId] });
      const previous = queryClient.getQueryData(['opportunity', opportunityId]);
      queryClient.setQueryData(['opportunity', opportunityId], (prev) => {
        if (!prev || !Array.isArray(prev.checklist)) return prev;
        const nextChecklist = prev.checklist.map((c) => (c.id === itemId
          ? { ...c, ...updates, completed_at: updates.status === 'completed' ? new Date().toISOString() : (updates.status ? null : c.completed_at) }
          : c));
        const completed = nextChecklist.filter((c) => c.status === 'completed').length;
        return {
          ...prev,
          checklist: nextChecklist,
          progress: prev.progress ? { ...prev.progress, completed, total: nextChecklist.length } : prev.progress,
        };
      });
      return { previous };
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) queryClient.setQueryData(['opportunity', opportunityId], ctx.previous); },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
      onUpdate?.();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (itemId) => apiClient.del(`/api/opportunities/${opportunityId}/checklist/${itemId}`),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: ['opportunity', opportunityId] });
      const previous = queryClient.getQueryData(['opportunity', opportunityId]);
      queryClient.setQueryData(['opportunity', opportunityId], (prev) => {
        if (!prev || !Array.isArray(prev.checklist)) return prev;
        const nextChecklist = prev.checklist.filter((c) => c.id !== itemId);
        const completed = nextChecklist.filter((c) => c.status === 'completed').length;
        return {
          ...prev,
          checklist: nextChecklist,
          progress: prev.progress ? { ...prev.progress, completed, total: nextChecklist.length } : prev.progress,
        };
      });
      return { previous };
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) queryClient.setQueryData(['opportunity', opportunityId], ctx.previous); },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
      onUpdate?.();
    },
  });

  const toggleItem = (item) => {
    const newStatus = item.status === 'completed' ? 'pending' : 'completed';
    updateMutation.mutate({ itemId: item.id, updates: { status: newStatus } });
  };

  const handleAddItem = (e) => {
    e.preventDefault();
    if (!newItem.title.trim()) return;
    addMutation.mutate(newItem);
  };

  // Group by category
  const grouped = checklist.reduce((acc, item) => {
    const cat = item.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Generate copy text
  const generateCopyText = () => {
    const lines = [];
    const completed = checklist.filter(c => c.status === 'completed');
    const pending = checklist.filter(c => c.status !== 'completed');

    lines.push(`CLOSE PLAN PROGRESS: ${completed.length}/${checklist.length} items complete`);
    lines.push('');

    for (const item of completed) {
      const date = item.completed_at ? formatDate(item.completed_at) : '';
      lines.push(`✓ ${item.title}${date ? ` (${date})` : ''}`);
    }

    for (const item of pending) {
      const due = item.due_date ? `Due: ${formatDate(item.due_date)}` : '';
      lines.push(`□ ${item.title}${due ? ` (${due})` : ''}`);
    }

    return lines.join('\n');
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const completedCount = progress?.completed || checklist.filter(c => c.status === 'completed').length;
  const totalCount = progress?.total || checklist.length;

  return (
    <div className="ccl-container">
      <div className="ccl-header">
        <div className="ccl-title-row">
          <h3 className="ccl-title">Close Checklist</h3>
          <span className="ccl-progress">{completedCount}/{totalCount}</span>
        </div>
        <div className="ccl-actions">
          <CopyButton
            text={generateCopyText()}
            label="Copy Progress"
            showLabel
            className="copy-btn-pill"
          />
          <button className="ccl-btn ccl-btn-add" onClick={() => setIsAdding(true)}>
            <Plus size={14} /> Add Item
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="ccl-progress-bar">
        <div
          className="ccl-progress-fill"
          style={{ width: `${totalCount ? (completedCount / totalCount) * 100 : 0}%` }}
        />
      </div>

      {/* Add new item form */}
      {isAdding && (
        <form className="ccl-add-form" onSubmit={handleAddItem}>
          <input
            type="text"
            placeholder="What needs to be done?"
            value={newItem.title}
            onChange={(e) => setNewItem(n => ({ ...n, title: e.target.value }))}
            autoFocus
          />
          <div className="ccl-add-row">
            <select
              value={newItem.category}
              onChange={(e) => setNewItem(n => ({ ...n, category: e.target.value }))}
            >
              <option value="">Category...</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select
              value={newItem.priority}
              onChange={(e) => setNewItem(n => ({ ...n, priority: e.target.value }))}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <input
              type="date"
              value={newItem.due_date}
              onChange={(e) => setNewItem(n => ({ ...n, due_date: e.target.value }))}
            />
          </div>
          <div className="ccl-add-actions">
            <button type="button" className="ccl-btn ccl-btn-cancel" onClick={() => setIsAdding(false)}>
              Cancel
            </button>
            <button type="submit" className="ccl-btn ccl-btn-save" disabled={addMutation.isPending || !newItem.title.trim()}>
              {addMutation.isPending ? <Loader2 size={14} className="ccl-spinner" /> : 'Add'}
            </button>
          </div>
        </form>
      )}

      {/* Checklist items grouped by category */}
      <div className="ccl-groups">
        {Object.entries(grouped).map(([category, items]) => {
          const catLabel = CATEGORIES.find(c => c.value === category)?.label || 'Other';
          const isExpanded = expandedCategories[category] !== false;
          const catCompleted = items.filter(i => i.status === 'completed').length;

          return (
            <div key={category} className="ccl-group">
              <button className="ccl-group-header" onClick={() => toggleCategory(category)}>
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="ccl-group-label">{catLabel}</span>
                <span className="ccl-group-count">{catCompleted}/{items.length}</span>
              </button>

              {isExpanded && (
                <div className="ccl-items">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className={`ccl-item ${item.status === 'completed' ? 'ccl-item-done' : ''}`}
                    >
                      <button
                        className="ccl-item-check"
                        onClick={() => toggleItem(item)}
                      >
                        {item.status === 'completed' ? (
                          <CheckSquare size={18} className="ccl-checked" />
                        ) : (
                          <Square size={18} />
                        )}
                      </button>

                      <div className="ccl-item-content">
                        <span className="ccl-item-title">{item.title}</span>
                        <div className="ccl-item-meta">
                          {item.priority && item.priority !== 'medium' && (
                            <span
                              className="ccl-item-priority"
                              style={{ color: PRIORITY_COLORS[item.priority] }}
                            >
                              {item.priority.toUpperCase()}
                            </span>
                          )}
                          {item.due_date && (
                            <span className="ccl-item-due">
                              <Calendar size={10} />
                              {formatDate(item.due_date)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="ccl-item-actions">
                        <CopyButton
                          text={`${item.status === 'completed' ? '✓' : '□'} ${item.title}${item.due_date ? ` (Due: ${formatDate(item.due_date)})` : ''}`}
                          size={12}
                        />
                        <button
                          className="ccl-item-delete"
                          onClick={() => deleteMutation.mutate(item.id)}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {checklist.length === 0 && !isAdding && (
          <div className="ccl-empty">
            No checklist items yet. Add items to track your close plan.
          </div>
        )}
      </div>
    </div>
  );
}

export default CloseChecklist;
